import type { Sequence } from "../types/Sequence"
import { IsNull } from "../checks/IsNull"
import { Zip } from "./Zip"

/**
 * Creates a record object from entries or from separate key and value sequences.
 *
 * @param entries - Iterable of [key, value] pairs
 * @returns Record object
 *
 * @example
 * ```ts
 * import { RecordCreate } from "@pdfking/tfunc"
 *
 * // From entries
 * const entries = [["name", "John"], ["age", 30]] as const
 * const record = RecordCreate(entries)
 * // { name: "John", age: 30 }
 *
 * // From separate keys and values
 * const keys = ["name", "age", "city"]
 * const values = ["John", 30, "NYC"]
 * const record2 = RecordCreate(keys, values)
 * // { name: "John", age: 30, city: "NYC" }
 * ```
 */
export function RecordCreate<
  const E extends readonly (readonly [PropertyKey, unknown])[],
>(entries: E): { [T in E[number] as T[0]]: T[1] }
export function RecordCreate<K extends PropertyKey, V>(
  entries: Sequence<readonly [K, V]>
): { [P in K]: V }
export function RecordCreate<const K extends readonly PropertyKey[], T>(
  keys: K,
  values: Sequence<T>
): { [P in K[number]]: T }
export function RecordCreate<K extends PropertyKey, T>(
  keys: Sequence<K>,
  values: Sequence<T>
): { [P in K]: T }
export function RecordCreate<T>(
  keysOrEntries: Sequence<string> | Sequence<readonly [PropertyKey, T]>,
  values?: Sequence<T>
): Record<string, T> {
  if (IsNull(values)) {
    return Object.fromEntries(keysOrEntries as Sequence<readonly [string, T]>)
  }
  return Object.fromEntries(Zip(keysOrEntries as Sequence<string>, values))
}
